import React, { useEffect, useState } from "react";

import InlineLoader from "./InlineLoader";
import CustomButton from "components/common/Button";
import { getExpectedFunds } from "services/billing";
import { CustomChip } from "./ui";
import ExpectedFundsCharges from "./ExpectedFundsCharges";
import { TierSwatch, TierCard } from "./ExpectedFundsShared";
import {
  EXPECTED_FUNDS_TIERS,
  ExpectedFundsBreakdown,
  ExpectedFundsResponse,
  ExpectedFundsRow,
  formatMoney,
  formatAmountShort,
  formatBankDate,
  formatRelativeDay,
  formatSyncedTime,
} from "./types";

const WINDOW_OPTIONS = [7, 14, 30];
const DEFAULT_LOOK_BACK = 7;
const DEFAULT_LOOK_AHEAD = 7;

const breakdownTotal = (b: ExpectedFundsBreakdown): number =>
  b.settledCents + b.inTransitCents + b.pendingCents + b.projectedCents;

/** Proportional tier bar — each segment carries the tier fill pattern. */
function BreakdownBar({ breakdown }: { breakdown: ExpectedFundsBreakdown }) {
  const total = breakdownTotal(breakdown);
  return (
    <div className="ef-bar">
      {total > 0 &&
        EXPECTED_FUNDS_TIERS.map((t) => {
          const value = breakdown[t.amountKey];
          if (value <= 0) return null;
          return (
            <div
              key={t.key}
              className={`ef-bar-seg ef-fill-${t.key}`}
              style={{ width: `${(value / total) * 100}%` }}
              title={`${t.label} ${formatMoney(value)}`}
            />
          );
        })}
    </div>
  );
}

function BreakdownLabels({ breakdown }: { breakdown: ExpectedFundsBreakdown }) {
  return (
    <div className="ef-bar-labels para-body-s-regular color-text-body-secondary">
      {EXPECTED_FUNDS_TIERS.map((t) => {
        const value = breakdown[t.amountKey];
        if (value <= 0) return null;
        return (
          <span key={t.key} className="ef-bar-label">
            <span className={`ef-glyph-${t.key}`} aria-hidden="true">
              {t.glyph}
            </span>{" "}
            {t.label} {formatAmountShort(value)}
          </span>
        );
      })}
    </div>
  );
}

function describeDay(row: ExpectedFundsRow, today: string): { estimated: boolean; secondary: string } {
  const b = row.breakdown ?? { settledCents: 0, inTransitCents: 0, pendingCents: 0, projectedCents: 0 };
  const estimated = !!row.estimated;
  const confirmed = !!row.confirmed;
  const settledOnly = confirmed && !estimated && b.inTransitCents + b.pendingCents === 0 && b.settledCents > 0;

  let word = "";
  if (estimated && confirmed) word = "confirmed · part estimated";
  else if (estimated) word = "estimated arrival";
  else if (settledOnly) word = "arrived";
  else if (confirmed) word = "confirmed";

  const relative = formatRelativeDay(row.date, today);
  return { estimated, secondary: word ? `${word} · ${relative}` : relative };
}

function DayRow({
  row,
  today,
  onView,
}: {
  row: ExpectedFundsRow;
  today: string;
  onView: (date: string) => void;
}) {
  const breakdown = row.breakdown ?? { settledCents: 0, inTransitCents: 0, pendingCents: 0, projectedCents: 0 };
  const { estimated, secondary } = describeDay(row, today);
  const isPast = !!row.date && row.date < today;
  const mainCents = isPast ? row.receivedCents ?? 0 : row.amountCents ?? 0;
  const showReceived = !isPast && (row.receivedCents ?? 0) > 0;
  const count = row.chargeCount ?? 0;

  return (
    <div className={`ef-row${row.isToday ? " ef-row--today" : ""}`}>
      <div className="ef-row-date">
        <span className={`para-body-s-medium color-text-heading-primary${estimated ? " ef-est-date" : ""}`}>
          {estimated ? "~ est. " : ""}
          {formatBankDate(row.date)}
        </span>
        <span className="para-body-xs-regular color-text-body-secondary">{secondary}</span>
      </div>

      <div>
        <BreakdownBar breakdown={breakdown} />
        <BreakdownLabels breakdown={breakdown} />
      </div>

      <div className="ef-amount">
        <p className="para-body-m-semibold color-text-heading-primary">{formatMoney(mainCents)}</p>
        {showReceived ? (
          <p className="para-body-xs-regular color-text-body-secondary">
            {formatMoney(row.receivedCents)} already received
          </p>
        ) : null}
      </div>

      <div>
        <CustomButton
          label={`View charges${count ? ` (${count})` : ""}`}
          variant="outlined"
          color="primary"
          size="small"
          isDisabled={count === 0}
          onClick={() => row.date && onView(row.date)}
        />
      </div>
    </div>
  );
}

function GapRow({ row }: { row: ExpectedFundsRow }) {
  const span =
    row.start && row.end && row.start !== row.end
      ? `${formatBankDate(row.start)} – ${formatBankDate(row.end)}`
      : formatBankDate(row.start);
  return (
    <div className="ef-gap para-body-s-regular color-text-body-secondary">
      <span className="ef-gap-dash" aria-hidden="true">
        —
      </span>
      <span>
        {span} · no settlement, {row.reason}
      </span>
    </div>
  );
}

/**
 * [PRD-2043] Expected funds — collections forecast. Replaces the visibility
 * finance lost with the Stripe Payouts screen: projected (not-yet-fired) money
 * merged with confirmed Stripe settlements, day by day.
 */
export default function ExpectedFundsTab() {
  const [lookBack, setLookBack] = useState(DEFAULT_LOOK_BACK);
  const [lookAhead, setLookAhead] = useState(DEFAULT_LOOK_AHEAD);
  const [data, setData] = useState<ExpectedFundsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getExpectedFunds({ lookBack, lookAhead });
        if (active) setData(res);
      } catch {
        if (active) setError("Failed to load the expected-funds forecast.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [lookBack, lookAhead]);

  if (selectedDate) {
    return <ExpectedFundsCharges date={selectedDate} onBack={() => setSelectedDate(null)} />;
  }

  const today = data?.today ?? "";
  const grandTotal = data ? breakdownTotal(data.totals) : 0;

  const renderRows = () => {
    if (!data) return null;
    const out: React.ReactNode[] = [];
    let bandInserted = false;

    // Header summary row: upcoming total (still-to-land over the look-ahead).
    out.push(
      <div className="ef-summary-row" key="upcoming">
        <span className="para-body-m-semibold color-text-heading-primary">Upcoming total</span>
        <span className="para-body-s-regular color-text-body-secondary">next {data.range.lookAheadDays} days</span>
        <span className="para-body-m-semibold color-text-heading-primary ef-amount">
          {formatMoney(data.totals.upcomingTotalCents)}
        </span>
      </div>
    );

    data.rows.forEach((row, idx) => {
      const repDate = row.type === "gap" ? row.end : row.date;
      if (!bandInserted && repDate && today && repDate <= today) {
        out.push(
          <div className="ef-today-band" key="today-band">
            <span className="para-body-s-semibold">TODAY · {formatBankDate(today)}</span>
            <span className="para-body-xs-regular">forecast above · today and actuals below</span>
          </div>
        );
        bandInserted = true;
      }
      out.push(
        row.type === "gap" ? (
          <GapRow row={row} key={`gap-${idx}`} />
        ) : (
          <DayRow row={row} today={today} onView={setSelectedDate} key={`day-${row.date}`} />
        )
      );
    });

    // Footer summary row: settled actuals over the look-back.
    out.push(
      <div className="ef-summary-row" key="arrived">
        <span className="para-body-m-semibold color-text-heading-primary">Arrived</span>
        <span className="para-body-s-regular color-text-body-secondary">
          settled actuals, last {data.range.lookBackDays} days
        </span>
        <span className="para-body-m-semibold color-text-heading-primary ef-amount">
          {formatMoney(data.totals.arrivedCents)}
        </span>
      </div>
    );

    return out;
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="ef-header">
        <div>
          <p className="heading-h3-semibold color-text-heading-primary">Expected funds</p>
          <p className="para-body-s-regular color-text-body-secondary">
            Money we expect to collect and when it lands in the operating account. Replaces the Stripe payouts
            forecast.
          </p>
        </div>
        {data ? (
          <span className="ef-synced para-body-s-regular color-text-body-secondary">
            Synced {formatSyncedTime(data.syncedAt)}
          </span>
        ) : null}
      </div>

      <div className="ef-range">
        <div className="ef-range-group">
          <span className="para-body-s-medium color-text-body-secondary">Look back</span>
          {WINDOW_OPTIONS.map((n) => (
            <CustomChip
              key={`back-${n}`}
              label={`${n} days`}
              size="small"
              onClick={() => setLookBack(n)}
              variant={lookBack === n ? "filled" : "outlined"}
              color={lookBack === n ? "primary" : "default"}
            />
          ))}
        </div>
        <div className="ef-range-group">
          <span className="para-body-s-medium color-text-body-secondary">Look ahead</span>
          {WINDOW_OPTIONS.map((n) => (
            <CustomChip
              key={`ahead-${n}`}
              label={`${n} days`}
              size="small"
              onClick={() => setLookAhead(n)}
              variant={lookAhead === n ? "filled" : "outlined"}
              color={lookAhead === n ? "primary" : "default"}
            />
          ))}
        </div>
      </div>

      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : data ? (
        <>
          <div className="ef-tier-cards">
            {EXPECTED_FUNDS_TIERS.map((t) => (
              <TierCard
                key={t.key}
                tier={t.key}
                amountCents={data.totals[t.amountKey]}
                caption={`${t.description} · ${grandTotal > 0 ? Math.round((data.totals[t.amountKey] / grandTotal) * 100) : 0}%`}
              />
            ))}
            <div className="ef-tier-card ef-tier-card--total">
              <div className="ef-tier-head">
                <span className="ef-swatch ef-swatch--total" aria-hidden="true" />
                <span aria-hidden="true">∑</span>
                <span className="para-body-s-medium color-text-heading-primary">Total</span>
              </div>
              <p className="heading-h3-semibold color-text-heading-primary mt-05">
                {formatMoney(data.totals.grandTotalCents)}
              </p>
              <p className="para-body-s-regular color-text-body-secondary">
                Settled + In transit + Pending + Projected
              </p>
            </div>
          </div>

          <p className="para-body-m-semibold color-text-heading-primary">Expected arrival date</p>
          <div className="ef-rows">{renderRows()}</div>

          <div className="ef-legend">
            {EXPECTED_FUNDS_TIERS.map((t) => (
              <div className="ef-legend-item" key={t.key}>
                <TierSwatch tier={t.key} />
                <div className="flex flex-col">
                  <span className="para-body-s-medium color-text-heading-primary">
                    <span className={`ef-glyph-${t.key}`} aria-hidden="true">
                      {t.glyph}
                    </span>{" "}
                    {t.label}
                  </span>
                  <span className="para-body-xs-regular color-text-body-secondary">{t.description}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
